import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatBubbleOvalLeftIcon, HandThumbUpIcon } from '@heroicons/react/24/outline';
import { formatNumberCompact } from '@shared/dream-components';

import type { CommunityPost } from '~/interfaces/community';
import { communityPostPath } from '~/routes/community/_utils/communityPostPath';
import { countReactions } from '~/routes/community/_utils/countReactions';
import { isResourceDiscussionHidden } from '~/routes/community/_utils/isResourceDiscussionHidden';

import { actionIconClass, countClass } from '../constants';
import { ShareMenu } from './ShareMenu';

interface PostCardCountsProps {
  post: CommunityPost;
}

export const PostCardCounts: FC<PostCardCountsProps> = ({ post }) => {
  const { t } = useTranslation();
  const isDiscussionHidden = isResourceDiscussionHidden(post);
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}${communityPostPath(post)}` : '';

  return (
    <div className="flex items-center justify-between px-2 pb-4">
      {isDiscussionHidden ? (
        <span aria-hidden="true" className="w-8" />
      ) : (
        <span className="flex h-8 items-center gap-1 px-2">
          <HandThumbUpIcon aria-hidden="true" className={actionIconClass} />
          <span className="sr-only">{t('community.content_page.reactions')}:</span>
          <span className={countClass}>{formatNumberCompact(countReactions(post.reactions_count))}</span>
        </span>
      )}
      {isDiscussionHidden ? (
        <span aria-hidden="true" className="w-8" />
      ) : (
        <span className="flex h-8 items-center gap-1 px-2">
          <ChatBubbleOvalLeftIcon aria-hidden="true" className={actionIconClass} />
          <span className="sr-only">{t('community.content_page.comments')}:</span>
          <span className={countClass}>{formatNumberCompact(post.comments_count)}</span>
        </span>
      )}
      <span aria-hidden="true" className="w-8" />
      <ShareMenu url={shareUrl} title={post.title} />
    </div>
  );
};
